import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { UserIcon, EnvelopeIcon, PencilIcon, CheckIcon, XMarkIcon, ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { getCardClasses, darkModeClasses, cn } from '../utils/darkModeClasses';
import { apiService } from '../services/api';
import { getApiErrorMessage } from '../constants/messages';
import { DeleteAccountPreviewResponse, UserIdentity } from '../types/api';
import Logger from '../utils/logger';
import { Button } from '../components/ui';

interface ProfileForm {
  prenom: string;
  nom: string;
  email: string;
}

const ProfilePage: React.FC = () => {
  const { t } = (useTranslation as any)('profile');
  const { user, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);

  // Methodes de connexion (portes du compte)
  const location = useLocation();
  const [identities, setIdentities] = useState<UserIdentity[]>([]);
  const [hasPassword, setHasPassword] = useState(true);
  const [identitiesLoading, setIdentitiesLoading] = useState(true);
  const [linkLoading, setLinkLoading] = useState(false);
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);
  const googleLoginEnabled =
    (window as { _env_?: { REACT_APP_GOOGLE_LOGIN_ENABLED?: string } })._env_?.REACT_APP_GOOGLE_LOGIN_ENABLED === 'true';

  const loadIdentities = useCallback(async () => {
    try {
      const res = await apiService.listIdentities();
      setIdentities(res.identities);
      setHasPassword(res.has_password);
    } catch (error: unknown) {
      Logger.error('Chargement des methodes de connexion:', error);
      toast.error(t('loginMethods.loadError'));
    } finally {
      setIdentitiesLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadIdentities();
  }, [loadIdentities]);

  // Retour de GoogleReturnPage apres une liaison depuis le profil (status=linked).
  useEffect(() => {
    const st = location.state as { googleLinked?: boolean; oidcLinked?: string } | null;
    if (st?.googleLinked || st?.oidcLinked) {
      toast.success(t('loginMethods.linked'));
      window.history.replaceState({}, '');
    }
  }, [location.state, t]);

  const handleLinkGoogle = async () => {
    setLinkLoading(true);
    try {
      const { authorization_url } = await apiService.startGoogleLink();
      window.location.href = authorization_url;
    } catch (error: unknown) {
      Logger.error('Association Google:', error);
      toast.error(t('loginMethods.linkError'));
      setLinkLoading(false);
    }
  };

  const handleUnlink = async (identity: UserIdentity) => {
    const providerLabel = t(`loginMethods.${identity.provider}`);
    if (!window.confirm(`${t('loginMethods.unlinkConfirmTitle')}\n${t('loginMethods.unlinkConfirmBody', { provider: providerLabel })}`)) {
      return;
    }
    setUnlinkingId(identity.id);
    try {
      await apiService.unlinkIdentity(identity.id);
      toast.success(t('loginMethods.unlinked'));
      await loadIdentities();
    } catch (error: unknown) {
      Logger.error('Dissociation:', error);
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (detail === 'last_identity') {
        toast.error(t('loginMethods.lastIdentity'));
      } else if (detail === 'cannot_unlink_password') {
        toast.error(t('loginMethods.cannotUnlinkPassword'));
      } else {
        toast.error(t('loginMethods.unlinkError'));
      }
    } finally {
      setUnlinkingId(null);
    }
  };

  const hasGoogle = identities.some((i) => i.provider === 'google');

  // Asset transfer preview state
  const [assetPreview, setAssetPreview] = useState<DeleteAccountPreviewResponse | null>(null);
  const [deleteStep, setDeleteStep] = useState<'initial' | 'preview' | 'confirm'>('initial');

  const { register, handleSubmit, formState: { errors }, reset } = useForm<ProfileForm>({
    defaultValues: {
      prenom: user?.prenom || '',
      nom: user?.nom || '',
      email: user?.email || ''
    }
  });

  const onSubmit = async (data: ProfileForm) => {
    setLoading(true);
    try {
      await updateProfile(data);
      toast.success(t('updateSuccess'));
      setIsEditing(false);
    } catch (error: unknown) {
      Logger.error('Erreur de mise à jour:', error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    reset({
      prenom: user?.prenom || '',
      nom: user?.nom || '',
      email: user?.email || ''
    });
    setIsEditing(false);
  };

  const handleDeleteAccount = async () => {
    // Step 1: Preview - Check if user has assets to transfer
    if (deleteStep === 'initial') {
      setDeleteLoading(true);
      try {
        const response = await apiService.deleteAccount(false); // Preview mode

        if (apiService.isDeleteAccountPreview(response)) {
          // User has assets that will be transferred
          if (response.warning && response.requires_confirmation) {
            setAssetPreview(response);
            setDeleteStep('preview');
          }
        } else {
          // No assets - show confirmation step directly
          setDeleteStep('confirm');
        }
      } catch (error: unknown) {
        Logger.error('Erreur lors de la vérification:', error);
        toast.error(getApiErrorMessage(error));
      } finally {
        setDeleteLoading(false);
      }
      return;
    }

    // Step 2: Confirm deletion
    if (deleteConfirmation !== 'SUPPRIMER') {
      toast.error(t('deleteModal.typeSUPPRIMER'));
      return;
    }

    setDeleteLoading(true);
    try {
      const response = await apiService.deleteAccount(true); // Confirm deletion

      if (!apiService.isDeleteAccountPreview(response)) {
        toast.success(response.message || 'Votre compte a été supprimé avec succès');
        logout();
        navigate('/login');
      }
    } catch (error: unknown) {
      Logger.error('Erreur lors de la suppression:', error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleProceedToConfirm = () => {
    setDeleteStep('confirm');
  };

  const resetDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteConfirmation('');
    setDeleteLoading(false);
    setAssetPreview(null);
    setDeleteStep('initial');
  };

  const handlePasswordReset = async () => {
    if (!user?.email) {
      toast.error(t('passwordModal.emailNotAvailable'));
      return;
    }

    setPasswordResetLoading(true);
    try {
      await apiService.requestPasswordReset({ email: user.email });
      toast.success(t('passwordModal.resetLinkSent'));
      setShowPasswordModal(false);
    } catch (error: unknown) {
      Logger.error('Erreur lors de la demande de réinitialisation:', error);
      toast.error(getApiErrorMessage(error));
    } finally {
      setPasswordResetLoading(false);
    }
  };

  return (
    <>
      {/* Header */}
      <div className={cn(darkModeClasses.card, darkModeClasses.border, "border-b")}>
        <div className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-12 w-12 rounded-full bg-accent-600 flex items-center justify-center">
                <UserIcon className="h-8 w-8 text-white" />
              </div>
              <div className="ml-4">
                <h1 className={cn(darkModeClasses.textPrimary, "text-2xl font-bold")}>
                  {t('title')}
                </h1>
                <p className={cn(darkModeClasses.textSecondary, "text-sm")}>
                  {t('subtitle')}
                </p>
              </div>
            </div>

            {!isEditing && (
              <Button
                variant="secondary"
                size="md"
                onClick={() => setIsEditing(true)}
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                {t('edit')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Info */}
          <div className="lg:col-span-2">
            <div className={getCardClasses()}>
              <div className="px-6 py-5">
                <h3 className={cn(darkModeClasses.textPrimary, "text-lg font-medium mb-6")}>
                  {t('personalInfo')}
                </h3>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Prénom */}
                  <div>
                    <label htmlFor="prenom" className={cn(darkModeClasses.textPrimary, "block text-sm font-medium mb-2")}>
                      {t('firstName')}
                    </label>
                    {isEditing ? (
                      <div>
                        <input
                          {...register('prenom', {
                            required: t('firstNameRequired') as string,
                            minLength: {
                              value: 2,
                              message: t('firstNameMinLength') as string
                            }
                          })}
                          id="prenom"
                          type="text"
                          aria-required="true"
                          aria-invalid={!!errors.prenom}
                          aria-describedby={errors.prenom ? "prenom-error" : undefined}
                          className={cn(
                            darkModeClasses.input,
                            darkModeClasses.focus,
                            "appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm transition-colors duration-200"
                          )}
                          placeholder={t('firstNamePlaceholder') as string}
                        />
                        {errors.prenom && (
                          <p id="prenom-error" className="mt-1 text-sm text-red-600" role="alert">{errors.prenom.message}</p>
                        )}
                      </div>
                    ) : (
                      <p className={cn(darkModeClasses.textSecondary, "text-sm py-2")}>
                        {user?.prenom || t('notProvided')}
                      </p>
                    )}
                  </div>

                  {/* Nom */}
                  <div>
                    <label htmlFor="nom" className={cn(darkModeClasses.textPrimary, "block text-sm font-medium mb-2")}>
                      {t('lastName')}
                    </label>
                    {isEditing ? (
                      <div>
                        <input
                          {...register('nom', {
                            required: t('lastNameRequired') as string,
                            minLength: {
                              value: 2,
                              message: t('lastNameMinLength') as string
                            }
                          })}
                          id="nom"
                          type="text"
                          aria-required="true"
                          aria-invalid={!!errors.nom}
                          aria-describedby={errors.nom ? "nom-error" : undefined}
                          className={cn(
                            darkModeClasses.input,
                            darkModeClasses.focus,
                            "appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm transition-colors duration-200"
                          )}
                          placeholder={t('lastNamePlaceholder') as string}
                        />
                        {errors.nom && (
                          <p id="nom-error" className="mt-1 text-sm text-red-600" role="alert">{errors.nom.message}</p>
                        )}
                      </div>
                    ) : (
                      <p className={cn(darkModeClasses.textSecondary, "text-sm py-2")}>
                        {user?.nom || t('notProvided')}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className={cn(darkModeClasses.textPrimary, "block text-sm font-medium mb-2")}>
                      {t('email')}
                    </label>
                    {isEditing ? (
                      <div>
                        <input
                          {...register('email', {
                            required: t('emailRequired') as string,
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: t('emailInvalid') as string
                            }
                          })}
                          id="email"
                          type="email"
                          aria-required="true"
                          aria-invalid={!!errors.email}
                          aria-describedby={errors.email ? "email-error" : undefined}
                          className={cn(
                            darkModeClasses.input,
                            darkModeClasses.focus,
                            "appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-text-quaternary focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm transition-colors duration-200"
                          )}
                          placeholder="votre@email.com"
                        />
                        {errors.email && (
                          <p id="email-error" className="mt-1 text-sm text-red-600" role="alert">{errors.email.message}</p>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <EnvelopeIcon className={cn(darkModeClasses.textMuted, "h-4 w-4 mr-2")} />
                        <p className={cn(darkModeClasses.textSecondary, "text-sm py-2")}>
                          {user?.email || t('notProvided')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {isEditing && (
                    <div className="flex items-center justify-end space-x-4 pt-4">
                      <Button
                        variant="secondary"
                        size="md"
                        onClick={handleCancel}
                      >
                        <XMarkIcon className="h-4 w-4 mr-2" />
                        {t('cancel')}
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        size="md"
                        loading={loading}
                      >
                        <CheckIcon className="h-4 w-4 mr-2" />
                        {loading ? t('saving') : t('save')}
                      </Button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Account Info */}
            <div className={getCardClasses()}>
              <div className="px-6 py-5">
                <h3 className={cn(darkModeClasses.textPrimary, "text-lg font-medium mb-4")}>
                  {t('accountInfo.title')}
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className={cn(darkModeClasses.textMuted, "text-xs uppercase tracking-wide")}>
                      {t('accountInfo.userId')}
                    </p>
                    <p className={cn(darkModeClasses.textSecondary, "text-sm mt-1 font-mono")}>
                      {user?.id || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={cn(darkModeClasses.textMuted, "text-xs uppercase tracking-wide")}>
                      {t('accountInfo.creationDate')}
                    </p>
                    <p className={cn(darkModeClasses.textSecondary, "text-sm mt-1")}>
                      {user?.date_creation ? new Date(user.date_creation).toLocaleDateString('fr-FR') : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={cn(darkModeClasses.textMuted, "text-xs uppercase tracking-wide")}>
                      {t('accountInfo.status')}
                    </p>
                    <div className="mt-1 flex items-center">
                      <div className="h-2 w-2 bg-green-400 rounded-full mr-2"></div>
                      <p className={cn(darkModeClasses.textSecondary, "text-sm")}>
                        {t('accountInfo.active')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Security */}
            <div className={getCardClasses()}>
              <div className="px-6 py-5">
                <h3 className={cn(darkModeClasses.textPrimary, "text-lg font-medium mb-4")}>
                  {t('security.title')}
                </h3>
                <div className="space-y-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowPasswordModal(true)}
                    className="w-full justify-start"
                  >
                    {t('security.changePassword')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full justify-start"
                  >
                    {t('security.twoFactor')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full justify-start"
                  >
                    {t('security.activeSessions')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Methodes de connexion (portes du compte) */}
            <div className={getCardClasses()}>
              <div className="px-6 py-5">
                <h3 className={cn(darkModeClasses.textPrimary, "text-lg font-medium mb-1")}>
                  {t('loginMethods.title')}
                </h3>
                <p className={cn(darkModeClasses.textSecondary, "text-sm mb-4")}>
                  {t('loginMethods.subtitle')}
                </p>
                {identitiesLoading ? (
                  <div className="animate-pulse h-10 rounded bg-surface-3" />
                ) : (
                  <ul className="space-y-2">
                    {identities.map((identity) => (
                      <li
                        key={identity.id}
                        className="flex items-center justify-between rounded border border-border-1 px-3 py-2"
                      >
                        <div>
                          <div className={cn(darkModeClasses.textPrimary, "text-sm font-medium")}>
                            {t(`loginMethods.${identity.provider}`)}
                          </div>
                          <div className={cn(darkModeClasses.textSecondary, "text-xs")}>
                            {identity.email_at_link || ''}
                            {identity.email_at_link ? ' · ' : ''}
                            {identity.last_login_at
                              ? t('loginMethods.lastLogin', { date: new Date(identity.last_login_at).toLocaleDateString() })
                              : t('loginMethods.never')}
                          </div>
                        </div>
                        {identity.provider !== 'password' && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleUnlink(identity)}
                            loading={unlinkingId === identity.id}
                            disabled={identities.length <= 1 || unlinkingId !== null}
                            title={identities.length <= 1 ? (t('loginMethods.lastIdentity') as string) : undefined}
                          >
                            {t('loginMethods.unlink')}
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {!identitiesLoading && !hasPassword && (
                  <p className={cn(darkModeClasses.textSecondary, "text-xs mt-3")}>
                    {t('loginMethods.noPasswordHint')}
                  </p>
                )}
                {googleLoginEnabled && !identitiesLoading && !hasGoogle && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleLinkGoogle}
                    loading={linkLoading}
                    className="w-full justify-start mt-3"
                  >
                    {t('loginMethods.linkGoogle')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Zone de danger */}
        <div className="mt-8">
          <div className={cn(getCardClasses(), "border-red-500/20")}>
            <div className="px-6 py-5">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-3" />
                <h3 className="text-lg font-medium text-red-400">
                  {t('dangerZone.title')}
                </h3>
              </div>

              <div className="mb-4">
                <p className={cn(darkModeClasses.textSecondary, "text-sm mb-2")}>
                  <span dangerouslySetInnerHTML={{ __html: t('dangerZone.description') as string }} />
                </p>
                <ul className="text-sm text-red-400 list-disc list-inside space-y-1 ml-4">
                  <li>{t('dangerZone.item1')}</li>
                  <li>{t('dangerZone.item2')}</li>
                  <li>{t('dangerZone.item3')}</li>
                  <li>{t('dangerZone.item4')}</li>
                  <li>{t('dangerZone.item5')}</li>
                  <li>{t('dangerZone.item6')}</li>
                </ul>
              </div>

              <div className="bg-red-500/12 border border-red-500/20 rounded-md p-4 mb-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mt-0.5" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-red-400">
                      {t('dangerZone.warning')}
                    </h4>
                    <p className="mt-1 text-sm text-red-400">
                      {t('dangerZone.warningText')}
                    </p>
                  </div>
                </div>
              </div>

              <Button
                variant="danger"
                size="md"
                onClick={() => setShowDeleteModal(true)}
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                {t('dangerZone.deleteAccount')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de changement de mot de passe */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="password-modal-title">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={() => setShowPasswordModal(false)} aria-hidden="true"></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-surface-2 rounded-card px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-500/12 sm:mx-0 sm:h-10 sm:w-10">
                  <UserIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 id="password-modal-title" className="text-lg leading-6 font-medium text-text-primary">
                    {t('passwordModal.title')}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-text-tertiary">
                      {t('passwordModal.description')}
                    </p>
                    <p className="text-sm font-medium text-text-primary mt-2">
                      {user?.email}
                    </p>
                    <p className="text-xs text-text-tertiary mt-2">
                      {t('passwordModal.linkInfo')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                <Button
                  variant="primary"
                  size="md"
                  onClick={handlePasswordReset}
                  loading={passwordResetLoading}
                  className="w-full sm:w-auto"
                >
                  {passwordResetLoading ? t('passwordModal.sending') : t('passwordModal.sendLink')}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={passwordResetLoading}
                  className="w-full sm:w-auto mt-3 sm:mt-0"
                >
                  {t('passwordModal.cancel')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression - Multi-step */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={resetDeleteModal} aria-hidden="true"></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-surface-2 rounded-card px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10 ${deleteStep === 'preview' ? 'bg-yellow-500/12' : 'bg-red-500/12'}`}>
                  <ExclamationTriangleIcon className={`h-6 w-6 ${deleteStep === 'preview' ? 'text-yellow-400' : 'text-red-400'}`} aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  {/* Step: Initial - Loading check */}
                  {deleteStep === 'initial' && (
                    <>
                      <h3 id="delete-modal-title" className="text-lg leading-6 font-medium text-text-primary">
                        {t('deleteModal.title')}
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-text-tertiary">
                          {t('deleteModal.checkMessage')}
                        </p>
                      </div>
                    </>
                  )}

                  {/* Step: Preview - Show assets to transfer */}
                  {deleteStep === 'preview' && assetPreview && (
                    <>
                      <h3 className="text-lg leading-6 font-medium text-text-primary">
                        {t('deleteModal.transferTitle')}
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-text-tertiary">
                          {assetPreview.message}
                        </p>

                        {/* Assets Summary */}
                        <div className="mt-4 p-4 bg-yellow-500/12 rounded-card border border-yellow-500/20">
                          <h4 className="text-sm font-medium text-text-primary mb-2">
                            {t('deleteModal.transferDescription')}
                          </h4>
                          <ul className="text-sm text-text-secondary space-y-1">
                            <li>• {t('deleteModal.projects', { count: assetPreview.assets_preview.project_count })}</li>
                            <li>• {t('deleteModal.environments', { count: assetPreview.assets_preview.environment_count })}</li>
                            <li>• {t('deleteModal.applications', { count: assetPreview.assets_preview.application_count })}</li>
                          </ul>

                          {assetPreview.assets_preview.projects.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-yellow-500/20">
                              <p className="text-xs text-text-tertiary mb-1">{t('deleteModal.affectedProjects')}</p>
                              <ul className="text-xs text-text-secondary">
                                {assetPreview.assets_preview.projects.map((project) => (
                                  <li key={project.id}>- {project.nom}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>

                        <p className="mt-4 text-sm text-yellow-400">
                          {t('deleteModal.gitlabWarning')}
                        </p>
                      </div>
                    </>
                  )}

                  {/* Step: Confirm - Type SUPPRIMER */}
                  {deleteStep === 'confirm' && (
                    <>
                      <h3 className="text-lg leading-6 font-medium text-text-primary">
                        {t('deleteModal.confirmTitle')}
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-text-tertiary">
                          <span dangerouslySetInnerHTML={{ __html: t('deleteModal.irreversible') as string }} />
                          {assetPreview ? (
                            t('deleteModal.withTransfer')
                          ) : (
                            t('deleteModal.withoutTransfer')
                          )}
                        </p>
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-text-secondary mb-2" dangerouslySetInnerHTML={{ __html: t('deleteModal.typeToConfirm') as string }}>

                          </label>
                          <input
                            type="text"
                            value={deleteConfirmation}
                            onChange={(e) => setDeleteConfirmation(e.target.value)}
                            className="block w-full px-3 py-2 border border-border-1 rounded-md shadow-sm placeholder-text-quaternary focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm bg-surface-3 text-text-primary"
                            placeholder="SUPPRIMER"
                            autoFocus
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                {/* Initial step - Check button */}
                {deleteStep === 'initial' && (
                  <Button
                    variant="danger"
                    size="md"
                    onClick={handleDeleteAccount}
                    loading={deleteLoading}
                    className="w-full sm:w-auto"
                  >
                    {deleteLoading ? t('deleteModal.checking') : t('deleteModal.continue')}
                  </Button>
                )}

                {/* Preview step - Proceed button */}
                {deleteStep === 'preview' && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleProceedToConfirm}
                    className="w-full sm:w-auto bg-yellow-600 hover:bg-yellow-700"
                  >
                    {t('deleteModal.understood')}
                  </Button>
                )}

                {/* Confirm step - Delete button */}
                {deleteStep === 'confirm' && (
                  <Button
                    variant="danger"
                    size="md"
                    onClick={handleDeleteAccount}
                    loading={deleteLoading}
                    disabled={deleteConfirmation !== 'SUPPRIMER'}
                    className="w-full sm:w-auto"
                  >
                    {deleteLoading
                      ? (assetPreview ? t('deleteModal.transferring') : t('deleteModal.deleting'))
                      : t('deleteModal.deletePermanently')}
                  </Button>
                )}

                <Button
                  variant="secondary"
                  size="md"
                  onClick={resetDeleteModal}
                  disabled={deleteLoading}
                  className="w-full sm:w-auto mt-3 sm:mt-0"
                >
                  {t('deleteModal.cancel')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfilePage;
